import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cx } from "../../lib/cx.js";

type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURATION_MS = 5000;

/**
 * Avisos breves en una región aria-live (los lectores de pantalla los anuncian sin
 * mover el foco). Los errores usan "assertive" y quedan más tiempo en pantalla.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setItems((all) => all.filter((t) => t.id !== id)), []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setItems((all) => [...all.slice(-3), { id, tone, message }]);
      setTimeout(() => dismiss(id), tone === "error" ? DURATION_MS * 2 : DURATION_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
    }),
    [push],
  );

  const icon = { success: CheckCircle2, error: AlertTriangle, info: Info } as const;
  const tone = {
    success: "border-success/30 [&>svg:first-child]:text-success",
    error: "border-danger/40 [&>svg:first-child]:text-danger",
    info: "border-border-strong [&>svg:first-child]:text-accent",
  } as const;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end">
        <div aria-live="polite" className="contents">
          {items.filter((t) => t.tone !== "error").map((t) => {
            const Icon = icon[t.tone];
            return (
              <ToastCard key={t.id} className={tone[t.tone]} onDismiss={() => dismiss(t.id)}>
                <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <p className="min-w-0 flex-1">{t.message}</p>
              </ToastCard>
            );
          })}
        </div>
        <div aria-live="assertive" className="contents">
          {items.filter((t) => t.tone === "error").map((t) => (
            <ToastCard key={t.id} className={tone.error} onDismiss={() => dismiss(t.id)}>
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <p className="min-w-0 flex-1">{t.message}</p>
            </ToastCard>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ children, className, onDismiss }: { children: ReactNode; className: string; onDismiss: () => void }) {
  return (
    <div
      className={cx(
        "pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-lg border bg-surface px-3.5 py-3 text-sm text-fg shadow-pop",
        className,
      )}
    >
      {children}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar aviso"
        className="-m-1 rounded p-1 text-subtle hover:bg-surface-2 hover:text-fg"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast fuera de <ToastProvider>");
  return ctx;
}
